import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import {
  DATA_CHANNEL_INIT,
  DATA_CHANNEL_NAMES,
  createClipboardInbox,
  evaluateSdpPolicy,
  parseChannelMessage,
  parseSignallingEnvelope,
  reviewClipboardSet,
  type DataChannelName,
  type Display,
  type FileMessage,
  type SignallingEnvelope,
} from "wylo-rc-session";
import type { Session } from "../api/parse.ts";
import { applyInboundFile, planOutboundFile, type InboundState } from "./files.ts";
import {
  assertCanOpen,
  buildCad,
  isPng,
  planClipboardBytes,
  buildHello,
  buildIceCandidate,
  buildKey,
  buildOffer,
  buildPointer,
  buildSelectDisplay,
  buildWheel,
  chooseDisplay,
  base64ToBytes,
  decodeClipboardText,
  iceConfigFor,
  requireSignalTarget,
} from "./messages.ts";
import type { CadResultError } from "./cad.ts";
import { primeVideo } from "./playback.ts";
import { ICE_DISCONNECT_GRACE_MS, initialIceLink, reduceIceLink, type IceEffect, type IceLink } from "./iceLink.ts";
import { readViewerStats, type ViewerCounters, type ViewerLink } from "./viewerStats.ts";
import { missLabel } from "./miss.ts";
import type { FileProgress } from "./model.ts";

export type SignalConnection = {
  start(): Promise<void>;
  send(envelope: SignallingEnvelope): Promise<void>;
  onMessage(handler: (value: unknown) => void): void;
  stop(): Promise<void>;
};

export type MediaPeer = {
  createOffer(): Promise<string>;
  setLocalDescription(sdp: string): Promise<void>;
  setRemoteDescription(sdp: string): Promise<void>;
  addIceCandidate(candidate: RTCIceCandidateInit): Promise<void>;
  onIceCandidate(handler: (candidate: RTCIceCandidate | null) => void): void;
  onTrack(handler: (stream: MediaStream) => void): void;
  onIceState(handler: (state: RTCIceConnectionState) => void): void;
  onConnectionState?(handler: (state: RTCPeerConnectionState) => void): void;
  onChannel(handler: (name: DataChannelName, data: string) => void): void;
  whenChannelsOpen(): Promise<void>;
  controlOpen(): boolean;
  onControlOpen(handler: (open: boolean) => void): void;
  channelOpen(name: DataChannelName): boolean;
  onChannelOpen(handler: (name: DataChannelName, open: boolean) => void): void;
  send(name: DataChannelName, json: string): void;
  readLink?(): Promise<ViewerLink | null>;
  close(): void;
};

export type LiveMedia = {
  displays: Display[];
  selectedDisplayId: string;
  selectDisplay(displayId: string): Promise<void>;
  sendClipboard(text: string): "sent" | "closed" | "disabled" | "limit" | "size-mismatch" | "bad-payload" | "invalid";
  sendPng(bytes: Uint8Array): "sent" | "closed" | "disabled" | "limit" | "size-mismatch" | "bad-payload" | "invalid";
  sendFile(bytes: Uint8Array, name: string): Promise<"sent" | "closed" | "disabled" | "limit" | "size-mismatch" | "bad-payload" | "invalid">;
  sendPointer(input: { x: number; y: number; buttons: number; action: "move" | "down" | "up" }): void;
  sendKey(input: { action: "down" | "up"; code: string; modifiers: Array<"Control" | "Alt" | "Shift" | "Meta"> }): void;
  sendWheel(input: { deltaX: number; deltaY: number }): void;
  sendCad(input: { requestId: string; ts: string }): { ok: true } | { ok: false; reason: "not_open" | "invalid" };
  attach(video: HTMLVideoElement): void;
  readLink?(): Promise<ViewerLink | null>;
  close(): void;
};

export function signalHub(apiBase: string, session: Session): { url: string; accessToken: string } {
  const base = apiBase.replace(/\/$/, "");
  if (!/^https?:\/\//.test(base)) throw new Error("apiBase must be an http(s) URL");
  requireSignalTarget(session);
  return { url: `${base}/rc`, accessToken: session.signalr.accessToken };
}

export function connectSignalR(session: Session, apiBase: string): SignalConnection {
  const hub = signalHub(apiBase, session);
  const target = requireSignalTarget(session);
  const connection = new HubConnectionBuilder()
    .withUrl(hub.url, {
      accessTokenFactory: () => hub.accessToken,
    })
    .configureLogging(LogLevel.Error)
    .build();
  return {
    start: () => connection.start(),
    send: (envelope) => connection.invoke(target.method, envelope),
    onMessage(handler) {
      connection.on(target.method, handler);
    },
    stop: () => connection.stop(),
  };
}

export function createBrowserPeer(config: RTCConfiguration): MediaPeer {
  const connection = new RTCPeerConnection(config);
  let counters: ViewerCounters | null = null;
  connection.addTransceiver("video", { direction: "recvonly" });
  const channels = new Map<DataChannelName, RTCDataChannel>();
  for (const name of DATA_CHANNEL_NAMES) {
    channels.set(name, connection.createDataChannel(name, DATA_CHANNEL_INIT));
  }
  return {
    createOffer: async () => {
      const offer = await connection.createOffer();
      if (!offer.sdp) throw new Error("offer did not include SDP");
      return offer.sdp;
    },
    setLocalDescription: async (sdp) => {
      await connection.setLocalDescription({ type: "offer", sdp });
    },
    setRemoteDescription: async (sdp) => {
      await connection.setRemoteDescription({ type: "answer", sdp });
    },
    addIceCandidate: (candidate) => connection.addIceCandidate(candidate),
    onIceCandidate(handler) {
      connection.addEventListener("icecandidate", (event) => handler(event.candidate));
    },
    onTrack(handler) {
      connection.addEventListener("track", (event) => {
        const stream = event.streams[0] ?? new MediaStream([event.track]);
        handler(stream);
      });
    },
    onIceState(handler) {
      connection.addEventListener("iceconnectionstatechange", () => {
        handler(connection.iceConnectionState);
      });
    },
    onConnectionState(handler) {
      connection.addEventListener("connectionstatechange", () => {
        handler(connection.connectionState);
      });
    },
    onChannel(handler) {
      for (const [name, channel] of channels) {
        channel.addEventListener("message", (event) => {
          if (typeof event.data === "string") handler(name, event.data);
        });
      }
    },
    whenChannelsOpen: () =>
      Promise.all(
        [...channels.values()].map(
          (channel) =>
            new Promise<void>((resolve) => {
              if (channel.readyState === "open") resolve();
              else channel.addEventListener("open", () => resolve(), { once: true });
            }),
        ),
      ).then(() => undefined),
    controlOpen() {
      return channels.get("control")?.readyState === "open";
    },
    onControlOpen(handler) {
      const channel = channels.get("control");
      if (!channel) return;
      const report = () => handler(channel.readyState === "open");
      channel.addEventListener("open", report);
      channel.addEventListener("close", report);
      report();
    },
    channelOpen(name) {
      return channels.get(name)?.readyState === "open";
    },
    onChannelOpen(handler) {
      for (const [name, channel] of channels) {
        const report = () => handler(name, channel.readyState === "open");
        channel.addEventListener("open", report);
        channel.addEventListener("close", report);
        report();
      }
    },
    send(name, json) {
      const channel = channels.get(name);
      if (!channel || channel.readyState !== "open") return;
      channel.send(json);
    },
    async readLink() {
      const report = await connection.getStats();
      const next = readViewerStats(report, counters);
      counters = next.counters;
      return next.link;
    },
    close() {
      connection.close();
    },
  };
}

type Waiter = (message: FileMessage | undefined) => void;

export async function openMedia(input: {
  session: Session;
  signal: SignalConnection;
  peer: MediaPeer;
  onDisplays: (displays: Display[], selectedDisplayId: string) => void;
  onClipboard: (text: string) => void;
  onClipboardPng?: (bytes: Uint8Array) => void;
  onFile: (file: FileProgress | null) => void;
  onSaveFile: (name: string, bytes: Uint8Array) => void;
  onFailed: (message: string) => void;
  onLink?: (status: "reconnecting" | "up") => void;
  onCloseable?: (close: () => void) => void;
  mediaBudgetMs?: number;
  schedule?: (run: () => void, ms: number) => void;
  onControlOpen?: (open: boolean) => void;
  onClipboardOpen?: (open: boolean) => void;
  onFilesOpen?: (open: boolean) => void;
  onCadResult?: (result: { requestId: string; ok: boolean; error: CadResultError | null }) => void;
}): Promise<LiveMedia> {
  assertCanOpen(input.session);
  const sentCandidates = new Set<string>();
  let displays: Display[] = [];
  let selectedDisplayId = "";
  let localDescriptionSet = false;
  let remoteDescriptionSet = false;
  let pendingAnswer: string | null = null;
  const pendingIce: RTCIceCandidateInit[] = [];
  let budget: ReturnType<typeof setTimeout> | undefined;
  let budgetToken = 0;
  let grace: ReturnType<typeof setTimeout> | undefined;
  let graceToken = 0;
  let link: IceLink = initialIceLink;
  let stream: MediaStream | null = null;
  let iceConnected = false;
  let frame = false;
  let video: HTMLVideoElement | null = null;
  let inbound: InboundState = { kind: "idle" };
  let outboundId: string | null = null;
  const waiters: Waiter[] = [];
  const clipboardInbox = createClipboardInbox();
  const clipboardEncoder = new TextEncoder();
  let closed = false;

  function bindVideo() {
    if (!video || !stream) return;
    if (video.srcObject === stream) return;
    video.srcObject = stream;
    primeVideo(video);
  }

  function noteFrame() {
    if (frame) return;
    frame = true;
    if (input.peer.controlOpen()) clearBudget();
  }

  function latchDecodedFrame(next: MediaStream) {
    if (typeof next.getVideoTracks !== "function") return;
    const track = next.getVideoTracks()[0];
    if (!track) return;
    if (track.muted === false) noteFrame();
    track.addEventListener("unmute", noteFrame);
  }

  function stamp() {
    return { id: crypto.randomUUID(), ts: new Date().toISOString() };
  }

  async function publish(envelope: SignallingEnvelope) {
    const parsed = parseSignallingEnvelope(envelope);
    if (!parsed.ok) throw new Error("signalling message failed validation");
    await input.signal.send(parsed.value);
  }

  const displaysReady = new Promise<Display[]>((resolve) => {
    input.signal.onMessage((value) => {
      void handleSignal(value, resolve);
    });
  });

  async function handleSignal(value: unknown, resolveDisplays: (displays: Display[]) => void) {
    const parsed = parseSignallingEnvelope(value);
    if (!parsed.ok || parsed.value.sessionId !== input.session.sessionId) return;
    const message = parsed.value;
    if (message.type === "displays") {
      displays = message.payload.displays;
      resolveDisplays(displays);
      if (selectedDisplayId) input.onDisplays(displays, selectedDisplayId);
      return;
    }
    if (message.type === "sdp-answer") {
      const policy = evaluateSdpPolicy(message.payload.sdp);
      if (!policy.ok) {
        input.onFailed("The answer failed the DTLS-SRTP check");
        close();
        return;
      }
      if (!localDescriptionSet) {
        pendingAnswer = message.payload.sdp;
        return;
      }
      await applyAnswer(message.payload.sdp);
      return;
    }
    if (message.type === "ice-candidate") {
      if (sentCandidates.has(message.payload.candidate)) return;
      if (!message.payload.candidate) return;
      if (message.payload.sdpMid == null && message.payload.sdpMLineIndex == null) return;
      await addRemoteIce({
        candidate: message.payload.candidate,
        sdpMid: message.payload.sdpMid,
        sdpMLineIndex: message.payload.sdpMLineIndex,
      });
    }
  }

  async function applyAnswer(sdp: string) {
    await input.peer.setRemoteDescription(sdp);
    remoteDescriptionSet = true;
    while (pendingIce.length > 0) {
      const queued = pendingIce.splice(0, pendingIce.length);
      for (const candidate of queued) {
        try {
          await input.peer.addIceCandidate(candidate);
        } catch {
          continue;
        }
      }
    }
  }

  async function addRemoteIce(candidate: RTCIceCandidateInit) {
    if (!remoteDescriptionSet) {
      pendingIce.push(candidate);
      return;
    }
    try {
      await input.peer.addIceCandidate(candidate);
    } catch {
      return;
    }
  }

  input.peer.onTrack((next) => {
    stream = next;
    bindVideo();
    latchDecodedFrame(next);
  });
  input.peer.onIceCandidate((candidate) => {
    const built = buildIceCandidate(input.session.sessionId, candidate);
    if (!built.ok || built.value.type !== "ice-candidate") return;
    sentCandidates.add(built.value.payload.candidate);
    void publish(built.value);
  });
  input.peer.onChannel((name, data) => {
    void handleChannel(name, data);
  });
  input.peer.onControlOpen((open) => {
    if (closed) return;
    if (open && frame) clearBudget();
    input.onControlOpen?.(open);
  });
  input.peer.onChannelOpen((name, open) => {
    if (closed) return;
    if (name === "clipboard") input.onClipboardOpen?.(open);
    if (name === "files") input.onFilesOpen?.(open);
  });
  input.peer.onIceState((state) => {
    observeLink("ice", state);
  });
  input.peer.onConnectionState?.((state) => {
    observeLink("connection", state);
  });

  input.onCloseable?.(close);
  await input.signal.start();
  const advertised = await displaysReady;
  const selected = chooseDisplay(advertised);
  if (!selected) throw new Error("the agent did not advertise a monitor");
  selectedDisplayId = selected.displayId;
  const select = buildSelectDisplay(input.session.sessionId, selected.displayId);
  if (!select.ok || select.value.type !== "select-display") throw new Error("select-display failed validation");
  await publish(select.value);
  const sdp = await input.peer.createOffer();
  const offer = buildOffer(input.session.sessionId, sdp);
  if (!offer.ok || offer.value.type !== "sdp-offer") {
    close();
    throw new Error("offer failed the DTLS-SRTP check");
  }
  await input.peer.setLocalDescription(offer.value.payload.sdp);
  localDescriptionSet = true;
  if (pendingAnswer) await applyAnswer(pendingAnswer);
  await publish(offer.value);
  armBudget();
  void input.peer.whenChannelsOpen().then(() => {
    const hello = buildHello(input.session, crypto.randomUUID(), new Date().toISOString());
    if (hello.ok) input.peer.send("control", JSON.stringify(hello.value));
  });
  input.onDisplays(advertised, selectedDisplayId);

  async function handleChannel(name: DataChannelName, data: string) {
    let json: unknown;
    try {
      json = JSON.parse(data);
    } catch {
      return;
    }
    const parsed = parseChannelMessage(name, json);
    if (!parsed.ok) return;
    const message = parsed.value;
    if (message.type === "displays.advertise") {
      displays = message.displays;
      input.onDisplays(displays, selectedDisplayId);
      return;
    }
    if (message.type === "displays.selected") {
      selectedDisplayId = message.displayId;
      input.onDisplays(displays, selectedDisplayId);
      return;
    }
    if (message.type === "input.cad.result") {
      if (message.sessionId !== input.session.sessionId) return;
      input.onCadResult?.({ requestId: message.requestId, ok: message.ok, error: message.error });
      return;
    }
    if (message.type === "clipboard.set") {
      const gathered = clipboardInbox.push(message);
      if (gathered.status !== "ready") return;
      const review = reviewClipboardSet({
        message: gathered.message,
        clipboardEnabled: input.session.features.clipboard,
      });
      if (!review.ok || gathered.message.direction !== "ToOperator") return;
      if (gathered.message.mime === "image/png") {
        const bytes = base64ToBytes(gathered.message.payloadBase64);
        if (bytes && isPng(bytes)) input.onClipboardPng?.(bytes);
        return;
      }
      const text = decodeClipboardText(gathered.message.payloadBase64);
      if (text !== undefined) input.onClipboard(text);
      return;
    }
    if (name !== "files" || !("transferId" in message)) return;
    if (outboundId && message.transferId === outboundId) {
      if (message.type === "file.offer" || message.type === "file.chunk" || message.type === "file.complete") return;
      const waiter = waiters.shift();
      if (waiter) waiter(message);
      return;
    }
    const step = await applyInboundFile(inbound, message, input.session.features.fileTransfer, inbound.kind === "receiving" ? 1 : 0, new Date().toISOString());
    inbound = step.state;
    if (step.reply) input.peer.send("files", JSON.stringify(step.reply));
    if (step.progress) {
      input.onFile({
        name: step.progress.name,
        bytes: step.progress.bytes,
        direction: "ToOperator",
        status: step.progress.status,
        reason: step.progress.reason,
      });
    }
    if (step.state.kind === "done") input.onSaveFile(step.state.name, step.state.bytes);
  }

  function watch() {
    return { iceConnected, frame };
  }

  function observeLink(signal: "ice" | "connection", state: string) {
    if (closed) return;
    if (state === "connected" || state === "completed") iceConnected = true;
    const reduced = reduceIceLink(link, { type: "state", signal, state });
    link = reduced.link;
    for (const effect of reduced.effects) applyLinkEffect(effect);
  }

  function applyLinkEffect(effect: IceEffect) {
    if (effect.type === "arm-grace") {
      armGrace();
      return;
    }
    if (effect.type === "clear-grace") {
      clearGrace();
      return;
    }
    if (effect.type === "show-reconnecting") {
      input.onLink?.("reconnecting");
      return;
    }
    if (effect.type === "hide-reconnecting") {
      input.onLink?.("up");
      return;
    }
    if (effect.type === "ice-fail") {
      input.onFailed(missLabel(watch(), "ice-failed"));
      close();
    }
  }

  function clearGrace() {
    graceToken += 1;
    if (grace !== undefined) clearTimeout(grace);
    grace = undefined;
  }

  function armGrace() {
    clearGrace();
    const token = graceToken;
    const run = () => {
      if (token !== graceToken || closed) return;
      const reduced = reduceIceLink(link, { type: "grace" });
      link = reduced.link;
      for (const effect of reduced.effects) applyLinkEffect(effect);
    };
    if (input.schedule) {
      input.schedule(run, ICE_DISCONNECT_GRACE_MS);
      return;
    }
    grace = setTimeout(run, ICE_DISCONNECT_GRACE_MS);
  }

  function clearBudget() {
    budgetToken += 1;
    if (budget !== undefined) clearTimeout(budget);
    budget = undefined;
  }

  function armBudget() {
    if (input.mediaBudgetMs === 0 || (input.peer.controlOpen() && frame)) return;
    const ms = input.mediaBudgetMs ?? 20_000;
    const token = budgetToken;
    const run = () => {
      if (token !== budgetToken || closed || (input.peer.controlOpen() && frame)) return;
      input.onFailed(missLabel(watch(), "budget"));
      close();
    };
    if (input.schedule) {
      input.schedule(run, ms);
      return;
    }
    budget = setTimeout(run, ms);
  }

  function close() {
    if (closed) return;
    closed = true;
    clearBudget();
    clearGrace();
    input.onControlOpen?.(false);
    for (const waiter of waiters) waiter(undefined);
    waiters.length = 0;
    input.peer.close();
    void input.signal.stop();
  }

  function waitFile(): Promise<FileMessage | undefined> {
    return new Promise((resolve) => {
      waiters.push(resolve);
    });
  }

  function sendClipboardBytes(bytes: Uint8Array, mime: "text/plain" | "image/png") {
    if (!input.peer.channelOpen("clipboard")) return "closed" as const;
    const planned = planClipboardBytes({
      enabled: input.session.features.clipboard,
      bytes,
      mime,
      direction: "ToDevice",
      ...stamp(),
    });
    if (!planned.ok) return planned.reason;
    for (const message of planned.messages) {
      input.peer.send("clipboard", JSON.stringify(message));
      if (!input.peer.channelOpen("clipboard")) return "closed" as const;
    }
    return "sent" as const;
  }

  return {
    displays: advertised,
    selectedDisplayId,
    async selectDisplay(displayId) {
      const next = buildSelectDisplay(input.session.sessionId, displayId);
      if (!next.ok || next.value.type !== "select-display") return;
      selectedDisplayId = displayId;
      await publish(next.value);
      input.onDisplays(displays, selectedDisplayId);
    },
    sendClipboard(text) {
      return sendClipboardBytes(clipboardEncoder.encode(text), "text/plain");
    },
    sendPng(bytes) {
      return sendClipboardBytes(bytes, "image/png");
    },
    async sendFile(bytes, name) {
      if (!input.peer.channelOpen("files")) return "closed";
      const transferId = crypto.randomUUID();
      const plan = await planOutboundFile({
        bytes,
        name,
        enabled: input.session.features.fileTransfer,
        activeTransfers: outboundId ? 1 : 0,
        transferId,
        now: new Date().toISOString(),
      });
      if (!plan.ok) return plan.reason;
      outboundId = transferId;
      input.onFile({ name, bytes: bytes.byteLength, direction: "ToDevice", status: "offering" });
      input.peer.send("files", JSON.stringify(plan.offer));
      const accepted = await waitFile();
      if (!accepted || accepted.type !== "file.accept") {
        outboundId = null;
        input.onFile({ name, bytes: bytes.byteLength, direction: "ToDevice", status: "aborted", reason: "rejected" });
        return "invalid";
      }
      input.onFile({ name, bytes: bytes.byteLength, direction: "ToDevice", status: "transferring" });
      for (const chunk of plan.chunks) input.peer.send("files", JSON.stringify(chunk));
      input.peer.send("files", JSON.stringify(plan.complete));
      const finished = await waitFile();
      outboundId = null;
      if (!finished || finished.type !== "file.ack") {
        input.onFile({ name, bytes: bytes.byteLength, direction: "ToDevice", status: "aborted", reason: "error" });
        return "invalid";
      }
      input.onFile({ name, bytes: bytes.byteLength, direction: "ToDevice", status: "complete" });
      return "sent";
    },
    sendPointer(pointer) {
      const built = buildPointer({ displayId: selectedDisplayId, ...pointer, ...stamp() });
      if (built.ok) input.peer.send("control", JSON.stringify(built.value));
    },
    sendKey(key) {
      const built = buildKey({ ...key, ...stamp() });
      if (built.ok) input.peer.send("control", JSON.stringify(built.value));
    },
    sendWheel(wheel) {
      const built = buildWheel({ displayId: selectedDisplayId, ...wheel, ...stamp() });
      if (built.ok) input.peer.send("control", JSON.stringify(built.value));
    },
    sendCad(cad) {
      if (closed || !input.peer.controlOpen()) return { ok: false, reason: "not_open" };
      const built = buildCad({ sessionId: input.session.sessionId, requestId: cad.requestId, ts: cad.ts });
      if (!built.ok || built.value.type !== "input.cad") return { ok: false, reason: "invalid" };
      input.peer.send("control", JSON.stringify(built.value));
      return { ok: true };
    },
    attach(next) {
      video = next;
      bindVideo();
    },
    readLink: () => input.peer.readLink?.() ?? Promise.resolve(null),
    close,
  };
}

export async function openBrowserMedia(
  session: Session,
  hooks: Omit<Parameters<typeof openMedia>[0], "session" | "signal" | "peer">,
  apiBase: string,
): Promise<LiveMedia> {
  const ice = iceConfigFor(session);
  if (!ice.ok) throw new Error(ice.reason);
  return openMedia({
    session,
    signal: connectSignalR(session, apiBase),
    peer: createBrowserPeer(ice.config),
    ...hooks,
  });
}
